import { type UseQueryResult, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from 'api/apiClient/apiClient'
import { mapBidResponseToRowValues } from 'views/IntradayTradingView/helpers/intradayBidSubmissions.helper'
import type { BidSubmissionResponse } from 'views/IntradayTradingView/IntradayTradingView.types'
import { LATEST_BID_SUBMISSIONS_QUERY_KEY, LATEST_INTRADAY_BATCH_ID_QUERY_KEY } from './elplan.constants'
import type { IntradayBatchIdReading, LatestIntradayBatchIdResponse } from './elplan.types'

type RowValues = Record<string, string>

type LatestBidSubmissionsSnapshot = {
  submissions: BidSubmissionResponse
  batchIdAtLoad: IntradayBatchIdReading
}

async function fetchLatestIntradayBatchId(systemId: number): Promise<IntradayBatchIdReading> {
  return apiClient<Partial<LatestIntradayBatchIdResponse> | null>(
    'elplan_bids/intraday/bid-submissions/latest-successful-batch-id',
    { method: 'GET', params: { system_id: systemId } }
  )
    .then((response) => {
      const batchId = response?.batch_id
      return typeof batchId === 'number' || batchId === null ? batchId : ('unknown' as const)
    })
    .catch(() => 'unknown' as const)
}

async function fetchLatestBidSubmissionsSnapshot(
  systemId: number,
  startTime: ISODateTime,
  endTime: ISODateTime
): Promise<LatestBidSubmissionsSnapshot> {
  const batchIdAtLoad = await fetchLatestIntradayBatchId(systemId)
  const submissions = await apiClient<BidSubmissionResponse>('elplan_bids/intraday/bid-submissions/latest', {
    method: 'GET',
    params: {
      systemId,
      startTime,
      endTime,
    },
  })
  return { submissions, batchIdAtLoad }
}

function latestBidSubmissionsQueryOptions(systemId: number, startTime: ISODateTime, endTime: ISODateTime) {
  return {
    queryKey: [LATEST_BID_SUBMISSIONS_QUERY_KEY, { systemId, startTime, endTime }],
    queryFn: () => fetchLatestBidSubmissionsSnapshot(systemId, startTime, endTime),
    retry: false,
    enabled: systemId > 0,
  }
}

export function useLatestBidSubmissions(
  systemId: number,
  startTime: ISODateTime,
  endTime: ISODateTime
): UseQueryResult<RowValues[], Error> {
  return useQuery({
    ...latestBidSubmissionsQueryOptions(systemId, startTime, endTime),
    select: (snapshot) => mapBidResponseToRowValues(snapshot.submissions, new Date(startTime)),
  })
}

export function useBatchIdAtLoad(
  systemId: number,
  startTime: ISODateTime,
  endTime: ISODateTime
): UseQueryResult<IntradayBatchIdReading, Error> {
  return useQuery({
    ...latestBidSubmissionsQueryOptions(systemId, startTime, endTime),
    select: (snapshot) => snapshot.batchIdAtLoad,
  })
}

export function useReadCurrentIntradayBatchId(): (systemId: number) => Promise<IntradayBatchIdReading> {
  const queryClient = useQueryClient()
  return (systemId) =>
    queryClient.fetchQuery({
      queryKey: [LATEST_INTRADAY_BATCH_ID_QUERY_KEY, { systemId }],
      queryFn: () => fetchLatestIntradayBatchId(systemId),
      staleTime: 0,
    })
}

export function useReloadLatestBidSubmissions(): (systemId: number) => Promise<void> {
  const queryClient = useQueryClient()
  return (systemId) => queryClient.resetQueries({ queryKey: [LATEST_BID_SUBMISSIONS_QUERY_KEY, { systemId }] })
}
